import React, { ReactNode, Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { pdfjs } from "react-pdf";
import { useGetDocumentQuery } from "../../../../services/accounting/uploadedDocument/api";
import { useGetAccountQuery } from "../../../../services/management/account/api";
import type { UnitFormat } from "../../../../services/management/units/api";
import AskPasswordStatus from "./ReactPdfViewer/AskPasswordStatus";
import PDFViewer, { PasswordPrompt } from "./ReactPdfViewer/PDFViewer";
// Shared by both engines, and statically imported on purpose: it has no @embedpdf deps, so it
// stays out of the lazy EmbedPdfViewer chunk below. Don't add @embedpdf imports to it.
import AnnotationsPanel from "./AnnotationsPanel";
import Toolbar from "./ReactPdfViewer/Toolbar";
import DownloadButton from "./ReactPdfViewer/DownloadButton";
import PageIndicator from "./ReactPdfViewer/PageIndicator";
import EngineToggle from "./EngineToggle";
import useViewerEngine from "./useViewerEngine";
import ViewerOverlay from "./ViewerOverlay";
import XmlViewer from "./XmlViewer";
import useTranslation from "../../../hooks/useTranslation";

// Lazy-loaded: the EmbedPDF engine + plugins + PDFium WASM are heavy and only needed
// when the experimental engine is toggled on, so keep them out of the default bundle.
const EmbedPdfViewer = lazy(() => import("./EmbedPdfViewer"));

export type DocumentFormat = UnitFormat;

const FILE_EXTENSIONS: Record<DocumentFormat, string> = {
    pdf: "pdf",
    isdoc: "isdoc",
    ubl: "xml",
};

interface Props {
    accountId: number | null;
    documentId: number | null;
    format?: DocumentFormat;
    allowDownload?: boolean;
    topLeft?: ReactNode;
    topRight?: ReactNode;
    bottomLeft?: ReactNode;
    bottomRight?: ReactNode;
    leftPanel?: ReactNode;
    rightPanel?: ReactNode;
}

const ASK_PASSWORD_RESERVE = "22rem";
const DEFAULT_SCALE = 1;
const ZOOM_STEP = 0.1;
const ROTATION_STEP = 90;

function passwordStatusFor(reason: number): number | null {
    if (reason === pdfjs.PasswordResponses.NEED_PASSWORD) {
        return AskPasswordStatus.REQUIRE_PASSWORD;
    }
    if (reason === pdfjs.PasswordResponses.INCORRECT_PASSWORD) {
        return AskPasswordStatus.WRONG_PASSWORD;
    }
    return null;
}

function isExpectedLoadError(error: Error & { status?: number }) {
    if (error.name === "InvalidPDFException") return true;

    return error.name === "ResponseException" && (error.status ?? 500) < 500;
}

function reportLoadError(error: Error, accountId: number | null, documentId: number | null) {
    if (isExpectedLoadError(error)) return;

    Sentry.captureException(error, {
        tags: { accountId: String(accountId), documentId: String(documentId) },
    });
}

function Spinner() {
    return (
        <div className="flex h-full w-full items-center justify-center bg-surface-2">
            <div
                role="status"
                aria-label="loading"
                className="h-8 w-8 animate-spin rounded-full border-2 border-accent/20 border-t-accent"
            />
        </div>
    );
}

export default function DocumentViewer({
    accountId,
    documentId,
    format = "pdf",
    allowDownload = false,
    topLeft,
    topRight,
    bottomLeft,
    bottomRight,
    leftPanel,
    rightPanel,
}: Props) {
    const { t } = useTranslation();
    const { data, currentData, isLoading } = useGetDocumentQuery(
        { accountId, documentId },
        { skip: !accountId || !documentId },
    );
    const { currentData: account, isLoading: isLoadingAccount } = useGetAccountQuery(accountId, {
        skip: !accountId,
    });

    const [engine, toggleEngine] = useViewerEngine();
    const [scale, setScale] = useState(DEFAULT_SCALE);
    const [rotation, setRotation] = useState(0);
    const [numPages, setNumPages] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const [failed, setFailed] = useState(false);
    const [progress, setProgress] = useState(0);
    const [passwordPrompt, setPasswordPrompt] = useState<PasswordPrompt | null>(null);
    const loadedRef = useRef(false);
    const promptAttempt = useRef(0);

    const file =
        accountId && documentId
            ? `/api/v1/accounting/${accountId}/uploaded_documents/${documentId}/file`
            : null;
    const tenantCode = account?.code?.replace(/[\\/:*?"<>|]/g, "-").trim();
    const fileName =
        tenantCode && documentId
            ? `${tenantCode}-${documentId}.${FILE_EXTENSIONS[format]}`
            : undefined;

    const reset = useCallback(() => {
        setScale(DEFAULT_SCALE);
        setRotation(0);
        setNumPages(0);
        setLoaded(false);
        setFailed(false);
        setProgress(0);
        setPasswordPrompt(null);
        loadedRef.current = false;
    }, []);

    useEffect(() => {
        reset();
    }, [file, engine, reset]);

    if (isLoading || isLoadingAccount) return <Spinner />;
    if (!data) return null;

    const onLoadSuccess = (pdf: { numPages: number }) => {
        setNumPages(pdf.numPages);
        setLoaded(true);
        setProgress(100);
        loadedRef.current = true;
        setPasswordPrompt(null);
    };

    const onLoadError = (error: Error) => {
        setFailed(true);
        setProgress(0);
        setPasswordPrompt(null);
        reportLoadError(error, accountId, documentId);
    };

    const onLoadProgress = ({ loaded: l, total }: { loaded: number; total: number }) => {
        if (loadedRef.current) return;

        setLoaded(false);
        setProgress(total ? (l / total) * 100 : 0);
    };

    const onPassword = (callback: (password: string | Error) => void, reason: number) => {
        const status = passwordStatusFor(reason);

        if (status === null) {
            callback(new Error(`Unexpected pdf.js password response: ${reason}`));
            return;
        }

        promptAttempt.current += 1;
        setPasswordPrompt({
            attempt: promptAttempt.current,
            status,
            verifyPassword: callback,
        });
    };

    const controlsDisabled = !!passwordPrompt || !loaded;

    const hasSidePanel = !!leftPanel || !!rightPanel;

    const engineToggle = <EngineToggle engine={engine} onToggle={toggleEngine} />;

    const xmlViewer =
        format === "pdf" ? null : (
            <XmlViewer
                key={file}
                file={file}
                fileName={fileName}
                format={format}
                accountId={accountId}
                documentId={documentId}
                topLeft={topLeft}
                topRight={topRight}
                bottomLeft={bottomLeft}
                bottomRight={bottomRight}
                annotations={
                    <AnnotationsPanel
                        accountId={accountId}
                        documentId={documentId}
                        note={currentData?.note}
                    />
                }
            />
        );

    return (
        <div className="flex h-full w-full flex-1">
            {leftPanel && (
                <aside className="flex-1 overflow-hidden border-border bg-surface md:w-96 md:flex-none md:shrink-0 md:border-r lg:w-[28rem] 2xl:w-[32rem]">
                    {leftPanel}
                </aside>
            )}
            <div
                className={[
                    "relative min-w-0 flex-1",
                    hasSidePanel ? "hidden md:flex" : "flex",
                ].join(" ")}
            >
                {xmlViewer ??
                    (engine === "embedpdf" ? (
                        <Suspense fallback={<Spinner />}>
                            <EmbedPdfViewer
                                file={file}
                                fileName={fileName}
                                allowDownload={allowDownload}
                                onSwitchEngine={toggleEngine}
                                topLeft={topLeft}
                                topRight={topRight}
                                annotations={(reserveTop) => (
                                    <AnnotationsPanel
                                        accountId={accountId}
                                        documentId={documentId}
                                        note={currentData?.note}
                                        layerClassName="z-[1000]"
                                        reserveTop={reserveTop}
                                    />
                                )}
                            />
                        </Suspense>
                    ) : (
                        <>
                            <PDFViewer
                                file={file}
                                scale={scale}
                                rotation={rotation}
                                numPages={numPages}
                                passwordPrompt={passwordPrompt}
                                onLoadSuccess={onLoadSuccess}
                                onLoadProgress={onLoadProgress}
                                onLoadError={onLoadError}
                                onPassword={onPassword}
                            />
                            {!loaded && !failed && !passwordPrompt && (
                                <div
                                    role="progressbar"
                                    aria-label={t("pdf_viewer.document.loading")}
                                    aria-valuenow={Math.round(progress)}
                                    aria-valuemin={0}
                                    aria-valuemax={100}
                                    className="pointer-events-none absolute left-0 right-0 top-0 z-10 h-0.5 bg-surface-3"
                                >
                                    <div
                                        className="h-full bg-accent transition-[width] duration-150"
                                        style={{ width: `${progress}%` }}
                                    />
                                </div>
                            )}
                            {numPages > 0 && <PageIndicator numPages={numPages} />}
                            <AnnotationsPanel
                                accountId={accountId}
                                documentId={documentId}
                                note={currentData?.note}
                                reserveTop={passwordPrompt ? ASK_PASSWORD_RESERVE : undefined}
                            />
                            <ViewerOverlay
                                topLeft={topLeft}
                                bottomLeft={bottomLeft}
                                bottomRight={bottomRight}
                            >
                                {topRight}
                                {engineToggle}
                                <Toolbar
                                    onZoomIn={() => setScale((s) => s + ZOOM_STEP)}
                                    onZoomOut={() =>
                                        setScale((s) => Math.max(ZOOM_STEP, s - ZOOM_STEP))
                                    }
                                    onRotate={() =>
                                        setRotation((r) => (r + ROTATION_STEP + 360) % 360)
                                    }
                                    disabled={controlsDisabled}
                                >
                                    {allowDownload && (
                                        <DownloadButton file={file} fileName={fileName} />
                                    )}
                                </Toolbar>
                            </ViewerOverlay>
                        </>
                    ))}
            </div>
            {rightPanel && (
                <aside className="flex-1 overflow-hidden border-border bg-surface md:w-96 md:flex-none md:shrink-0 md:border-l lg:w-[28rem] 2xl:w-[32rem]">
                    {rightPanel}
                </aside>
            )}
        </div>
    );
}
